import React, { useEffect, useMemo, useState } from 'react';
import planoService from '../services/planoTrabalhoService';
import userAdminService, { type ProcuradorJuridicoDTO } from '../services/userAdminService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import DashboardPagination from '../components/DashboardPagination';
import { FIELD_BASE_CLASS, FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

const formatDateTime = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type FilaKey = 'distribuir' | 'distribuidos' | 'aguardando_envio' | 'enviados';

// Rótulos/badges da coluna Status/Fila.
const FILA_LABEL: Record<FilaKey, string> = {
  distribuir: 'A Distribuir',
  distribuidos: 'Distribuído',
  aguardando_envio: 'Aguardando Envio',
  enviados: 'Concluído',
};

// Nome curto da data usada no filtro para cada fila (vira o rótulo dos campos de data).
const FILA_DATE_FIELD: Record<FilaKey, string> = {
  distribuir: 'Envio à PGM',
  distribuidos: 'Distribuição',
  aguardando_envio: 'Distribuição',
  enviados: 'Conclusão do parecer',
};

const FILA_BADGE: Record<FilaKey, string> = {
  distribuir: 'border-warning-line bg-warning-soft text-warning-text',
  distribuidos: 'border-info-line bg-info-soft text-info-fg',
  aguardando_envio: 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-800 dark:text-indigo-300',
  enviados: 'border-success-line bg-success-soft text-success-text',
};

// Data relevante para o filtro/ordenação conforme a fila em que o plano está.
const filaDateStr = (p: PlanoTrabalho, fila: FilaKey): string | null | undefined => {
  switch (fila) {
    case 'distribuir': return p.enviadoPgmEm;
    case 'distribuidos':
    case 'aguardando_envio': return p.procuradorJuridicoVinculadoEm;
    case 'enviados': return p.concluidoEm;
  }
};

// Classifica um plano na fila jurídica correspondente.
const getFilaKey = (p: PlanoTrabalho): FilaKey | null => {
  if (p.etapaAtual === 'PROCURADORIA_JURIDICA') return p.procuradorJuridicoId ? 'distribuidos' : 'distribuir';
  if (p.etapaAtual === 'GESTOR_JURIDICO_REVISAO') return 'aguardando_envio';
  if (p.etapaAtual === 'CONVENIOS_FORMALIZACAO' || p.etapaAtual === 'CONCLUIDO' || p.status === 'CONCLUIDO') return 'enviados';
  return null;
};

const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const PAGE_SIZE = 10;

interface RelatorioPareceresPageProps {
  /** Quando renderizado como aba dentro da Fila Jurídica, oculta o cabeçalho próprio. */
  embedded?: boolean;
}

const RelatorioPareceresPage: React.FC<RelatorioPareceresPageProps> = ({ embedded = false }) => {
  const [planosTodos, setPlanosTodos] = useState<PlanoTrabalho[]>([]);
  const [procuradores, setProcuradores] = useState<ProcuradorJuridicoDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const [dataInicial, setDataInicial] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toInputDate(d);
  });
  const [dataFinal, setDataFinal] = useState(() => toInputDate(new Date()));
  const [procuradorId, setProcuradorId] = useState(''); // '' = Todos
  const [filaFiltro, setFilaFiltro] = useState<FilaKey>('distribuir');

  // Filtros efetivamente aplicados — só mudam ao clicar em "Buscar". null = nenhuma busca ainda.
  const [applied, setApplied] = useState<
    { fila: FilaKey; inicio: string; fim: string; procuradorId: string } | null
  >(null);

  useEffect(() => {
    userAdminService.listProcuradoresJuridicos()
      .then(users => setProcuradores(Array.isArray(users) ? users : []))
      .catch(() => {});
  }, []);

  const handleBuscar = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await planoService.listAll({ minhaFila: false });
      setPlanosTodos(Array.isArray(data) ? data : []);
      setApplied({ fila: filaFiltro, inicio: dataInicial, fim: dataFinal, procuradorId });
      setPage(1);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nao foi possivel carregar os pareceres.');
    } finally {
      setLoading(false);
    }
  };

  // Relação de pareceres: aplica os filtros CONFIRMADOS (applied) sobre os planos da fase jurídica.
  // A data considerada depende da fila do plano:
  //  - A Distribuir      → data em que o Convênio enviou o plano para a PGM (enviadoPgmEm)
  //  - Distribuído       → data em que foi distribuído ao procurador (procuradorJuridicoVinculadoEm)
  //  - Aguardando Envio  → data em que foi distribuído ao procurador (procuradorJuridicoVinculadoEm)
  //  - Concluído         → data em que o parecer foi concluído (concluidoEm)
  const pareceres = useMemo(() => {
    if (!applied) return [];
    const inicio = applied.inicio ? new Date(`${applied.inicio}T00:00:00`) : null;
    const fim = applied.fim ? new Date(`${applied.fim}T23:59:59`) : null;
    return planosTodos
      .map(plano => ({ plano, fila: getFilaKey(plano) }))
      .filter((row): row is { plano: PlanoTrabalho; fila: FilaKey } =>
        row.fila !== null && row.fila === applied.fila)
      .filter(({ plano, fila }) => {
        const baseStr = filaDateStr(plano, fila);
        const base = baseStr ? new Date(baseStr) : null;
        if (inicio && (!base || base < inicio)) return false;
        if (fim && (!base || base > fim)) return false;
        if (applied.procuradorId) {
          const matches = String(plano.procuradorJuridicoId ?? '') === applied.procuradorId
            || String(plano.auxiliarJuridicoId ?? '') === applied.procuradorId;
          if (!matches) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aDate = filaDateStr(a.plano, a.fila);
        const bDate = filaDateStr(b.plano, b.fila);
        return new Date(bDate || 0).getTime() - new Date(aDate || 0).getTime();
      });
  }, [planosTodos, applied]);

  const totalPages = Math.max(1, Math.ceil(pareceres.length / PAGE_SIZE));
  const pageRows = pareceres.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Rótulos dos campos de data mudam conforme a fila, deixando explícito qual data é filtrada.
  const dateFieldName = FILA_DATE_FIELD[filaFiltro];
  const dataInicialLabel = `${dateFieldName} (inicial)`;
  const dataFinalLabel = `${dateFieldName} (final)`;

  const handleExportCsv = () => {
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const header = ['Plano', 'Instituição', 'Protocolo', 'Status/Fila', 'Envio à PGM', 'Distribuído em',
      'Distribuído por', 'Procurador', 'Concluído em', 'Nº parecer', 'Ano'];
    const rows = pareceres.map(({ plano, fila }) => [
      plano.titulo || 'Plano de Trabalho',
      plano.instituicaoNome || '',
      plano.numeroProtocolo || '',
      FILA_LABEL[fila],
      formatDateTime(plano.enviadoPgmEm) || '',
      formatDateTime(plano.procuradorJuridicoVinculadoEm) || '',
      plano.distribuidoPor || '',
      plano.procuradorJuridicoNome || '',
      formatDateTime(plano.concluidoEm) || '',
      plano.numeroParecer || '',
      plano.anoParecer != null ? String(plano.anoParecer) : '',
    ]);
    const csv = String.fromCharCode(0xFEFF) + [header, ...rows]
      .map(cols => cols.map(v => escape(String(v))).join(';'))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relacao-pareceres-${applied?.inicio ?? dataInicial}-a-${applied?.fim ?? dataFinal}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full space-y-5">
      {embedded ? (
        !loading && applied && (
          <div className="flex justify-end">
            <span className="inline-flex w-fit items-center rounded border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              {pareceres.length} parecer{pareceres.length === 1 ? '' : 'es'}
              {totalPages > 1 && ` · pág. ${page}/${totalPages}`}
            </span>
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-blue-600 dark:text-blue-300">
              Relatórios
            </p>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Relatório</h1>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Pareceres da fila jurídica no período e filtros selecionados.
            </p>
          </div>
          {!loading && applied && (
            <span className="inline-flex w-fit items-center rounded border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              {pareceres.length} parecer{pareceres.length === 1 ? '' : 'es'}
              {totalPages > 1 && ` · pág. ${page}/${totalPages}`}
            </span>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Status / Fila</label>
            <select
              value={filaFiltro}
              onChange={(e) => setFilaFiltro(e.target.value as FilaKey)}
              className={`${FIELD_BASE_CLASS} w-48`}
            >
              {(['distribuir', 'distribuidos', 'aguardando_envio', 'enviados'] as FilaKey[]).map(key => (
                <option key={key} value={key}>{FILA_LABEL[key]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">{dataInicialLabel}</label>
            <input
              type="date"
              value={dataInicial}
              max={dataFinal || undefined}
              onChange={(e) => setDataInicial(e.target.value)}
              className={FIELD_BASE_CLASS}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">{dataFinalLabel}</label>
            <input
              type="date"
              value={dataFinal}
              min={dataInicial || undefined}
              onChange={(e) => setDataFinal(e.target.value)}
              className={FIELD_BASE_CLASS}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Assessor / Procurador</label>
            <select
              value={procuradorId}
              onChange={(e) => setProcuradorId(e.target.value)}
              className={`${FIELD_BASE_CLASS} w-56`}
            >
              <option value="">Todos</option>
              {procuradores.map(procurador => (
                <option key={procurador.id} value={procurador.id}>
                  {procurador.role === 'JURIDICO_AUXILIAR'
                    ? `Auxiliar: ${procurador.nomeCompleto || procurador.email}`
                    : (procurador.nomeCompleto || procurador.email)}
                </option>
              ))}
            </select>
          </div>
          <Button variant="primary" className="ml-auto" onClick={handleBuscar} disabled={loading}>
            {loading ? 'Carregando...' : 'Buscar'}
          </Button>
          <Button variant="success" onClick={handleExportCsv} disabled={pareceres.length === 0}>
            Exportar CSV
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-fg-muted">Carregando pareceres...</p>
        </div>
      ) : !error && !applied ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-base font-semibold text-fg">Defina os filtros e clique em Buscar</h2>
          <p className="mt-2 text-sm text-fg-muted">
            Selecione a fila, o período e o procurador desejados e clique em <span className="font-medium">Buscar</span> para listar os pareceres.
          </p>
        </div>
      ) : !error && pareceres.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-950">
          <h2 className="text-base font-semibold text-fg">Nenhum parecer para os filtros aplicados</h2>
          <p className="mt-2 text-sm text-fg-muted">
            Ajuste o período, o procurador ou a fila selecionada.
          </p>
        </div>
      ) : !error ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-800 dark:bg-slate-950 dark:[&_tbody_tr]:border-slate-800 dark:[&_tbody_tr:hover]:bg-slate-900/70 dark:[&_td]:text-slate-300 dark:[&_th]:border-slate-800 dark:[&_th]:text-slate-400 dark:[&_thead_tr]:border-slate-800">
          <div className="overflow-hidden">
            <Table>
              <Thead>
                <Tr className="border-b border-line">
                  <Th className="w-[14%] text-fg-muted">Plano</Th>
                  <Th className="w-[14%] text-fg-muted">Instituição</Th>
                  <Th className="w-[9%] text-fg-muted">Protocolo</Th>
                  <Th className="w-[10%] text-fg-muted">Status/Fila</Th>
                  <Th className="w-[9%] text-fg-muted">Envio à PGM</Th>
                  <Th className="w-[9%] text-fg-muted">Distribuído em</Th>
                  <Th className="w-[9%] text-fg-muted">Distribuído por</Th>
                  <Th className="w-[9%] text-fg-muted">Procurador</Th>
                  <Th className="w-[9%] text-fg-muted">Concluído em</Th>
                  <Th className="w-[5%] text-fg-muted">Nº parecer</Th>
                  <Th className="w-[3%] text-fg-muted">Ano</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pageRows.map(({ plano, fila }) => (
                  <Tr key={plano.id} className="group border-b border-gray-100 last:border-b-0">
                    <Td className="break-words">
                      <div className="font-medium leading-snug text-fg" title={plano.titulo || 'Plano de Trabalho'}>{plano.titulo || 'Plano de Trabalho'}</div>
                      {plano.emendaCodigo && (
                        <div className="mt-0.5 text-xs text-fg-subtle">Emenda {plano.emendaCodigo}</div>
                      )}
                    </Td>
                    <Td className="break-words text-fg">{plano.instituicaoNome || '-'}</Td>
                    <Td className="break-words text-fg">{plano.numeroProtocolo || '-'}</Td>
                    <Td>
                      <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-medium ${FILA_BADGE[fila]}`}>
                        {FILA_LABEL[fila]}
                      </span>
                    </Td>
                    <Td className="text-fg">{formatDateTime(plano.enviadoPgmEm) || '-'}</Td>
                    <Td className="text-fg">{formatDateTime(plano.procuradorJuridicoVinculadoEm) || '-'}</Td>
                    <Td className="break-words text-fg">{plano.distribuidoPor || '-'}</Td>
                    <Td className="break-words text-fg">{plano.procuradorJuridicoNome || '-'}</Td>
                    <Td className="text-fg">{formatDateTime(plano.concluidoEm) || '-'}</Td>
                    <Td className="break-words text-fg">{plano.numeroParecer || '-'}</Td>
                    <Td className="break-words text-fg">{plano.anoParecer ?? '-'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
          {totalPages > 1 && (
            <DashboardPagination
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={pareceres.length}
              onPageChange={setPage}
              itemLabel="pareceres"
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RelatorioPareceresPage;
